'use client';

import { Box, Container, Stack, Text, Button, Field, Input, Link, Dialog, Portal, Alert } from '@chakra-ui/react';
import React, { useState } from 'react';
import { CanLogoIcon } from '@/../public/assets/icons';
import { forgotPasswordStyles } from './forgot-password.styles';
import Image from 'next/image';
import { forgotPasswordSchema, TForgotPassword } from './forgot-password.validator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPassword } from '@/services/auth/auth.service';

const ForgotPasswordForm = () => {
   const [isOpen, setIsOpen] = useState<boolean>(false);
   const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      setError,
      watch,
   } = useForm<TForgotPassword>({
      defaultValues: {
         email: '',
      },
      resolver: zodResolver(forgotPasswordSchema),
   });

   const onSubmit = handleSubmit(async (value: TForgotPassword) => {
      const [error] = await forgotPassword(value.email);

      if (error) {
         console.log(error);
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      setIsOpen(true);
   });

   const email = watch('email');

   return (
      <Container css={forgotPasswordStyles.container}>
         <Box css={forgotPasswordStyles.reset_area}>
            <Box css={forgotPasswordStyles.reset_box}>
               <CanLogoIcon />
               <Text as="h2" color="white" textStyle="4xl" fontWeight="bold">
                  Recover your Password
               </Text>
               <Text color="darkGray" textStyle="md" textAlign="center" mt="-10px">
                  Enter your email address below to receive reset password link
               </Text>

               {errors.root?.message && (
                  <Alert.Root status="error">
                     <Alert.Indicator />
                     <Alert.Title>{errors.root.message}</Alert.Title>
                  </Alert.Root>
               )}
               <Box as="form" css={forgotPasswordStyles.reset_form} onSubmit={onSubmit}>
                  <Stack gap="4">
                     <Field.Root invalid={!!errors.email}>
                        <Field.Label textStyle="xs" color="white" mb="1">
                           Email
                           <Box as="span" color="#f00">
                              *
                           </Box>
                        </Field.Label>
                        <Input
                           {...register('email')}
                           placeholder="Email"
                           variant="subtle"
                           h="10"
                           rounded="12px"
                           bg="#202020"
                        />
                        <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
                     </Field.Root>
                     {/* <Button type="submit" h="10" m="0 auto" mt="3">Login</Button> */}
                     <Button type="submit" h="10" m="0 auto" mt="3" disabled={isSubmitting || !email}>
                        Recover
                     </Button>
                     <Dialog.Root
                        open={isOpen}
                        lazyMount
                        onOpenChange={({ open }) => setIsOpen(open)}
                        placement="center"
                        motionPreset="slide-in-bottom"
                     >
                        <Portal>
                           <Dialog.Backdrop />
                           <Dialog.Positioner>
                              <Dialog.Content bg="#202020" rounded="15px" py="4" maxWidth="340px">
                                 <Dialog.Body>
                                    <Box
                                       w="16"
                                       h="16"
                                       rounded="50%"
                                       bg="#f5e87614"
                                       m="0 auto 10px"
                                       display="flex"
                                       alignItems="center"
                                       justifyContent="center"
                                    >
                                       <Image src="/assets/images/email_icon.svg" alt="" width="30" height="30" />
                                    </Box>
                                    <Dialog.Title color="white" textStyle="xl" textAlign="center" mb="2">
                                       Check Your email
                                    </Dialog.Title>
                                    <Text color="darkGray" textStyle="sm" textAlign="center">
                                       We’ve sent you an email with the necessary instructions to continue
                                    </Text>
                                 </Dialog.Body>
                                 <Dialog.Footer justifyContent="center" pt="0">
                                    <Dialog.ActionTrigger asChild>
                                       <Button variant="outline" h="10">
                                          OK
                                       </Button>
                                    </Dialog.ActionTrigger>
                                 </Dialog.Footer>
                              </Dialog.Content>
                           </Dialog.Positioner>
                        </Portal>
                     </Dialog.Root>

                     <Box textAlign="center" w="full" mt="-3" mb="2">
                        <Text color="white" fontSize="sm" fontWeight="600">
                           Go back to
                           <Link href="/auth/login" textDecoration="underline" color="brand" ml="2" mt="4">
                              Login
                           </Link>
                        </Text>
                     </Box>
                  </Stack>
               </Box>
            </Box>
         </Box>
      </Container>
   );
};

export default ForgotPasswordForm;
